import React, { forwardRef, memo, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { IErrMsg } from './types'
import { DescriptionBtnWithPopup } from '../buttons/PromptBtn/DescriptionBtnWithPopup'
import { RequiredMark } from '../ui/RequiredMark/RequiredMark'
import { RiskyParamUnlockBtn } from './components/RiskyParam/RiskyParamUnlockBtn'

export interface IStringInput {
    state: any
    name: string
    label: string
    tooltipText: string
    placeholder: string
    changeWarning?: string
    errMsg: IErrMsg
    isRequired: boolean
    className?: string
    isColumn?: boolean
    touched?: any
    errors?: any
    isDisabled?: boolean
    isLabelHidden?: boolean
    isEmail?: boolean
    isFeedbackFixed?: boolean

    onEnter?: Function
    onBlur?: Function
    onChange?: Function
    setState(state: any): any
    setTouched?(touched: any): any
}

export const StringInput = memo(
    forwardRef<HTMLInputElement, IStringInput>(function StringInput(
        {
            state,
            name,
            label,
            tooltipText,
            placeholder,
            changeWarning,
            errMsg,
            isRequired,
            className,
            touched,
            errors,
            isDisabled,
            isColumn,
            isLabelHidden,
            isEmail,
            isFeedbackFixed,
            onEnter,
            onBlur,
            onChange,
            setState,
            setTouched,
        },
        ref
    ) {
        const [isRiskyParam, setIsRiskyParam] = useState(!!changeWarning)

        const handleUnlockRiskyParam = () => setIsRiskyParam(false)

        const handleChange = (event: any): void => {
            setTouched && setTouched({ ...touched, [name]: true })
            setState({ ...state, [name]: event.target.value })
            if (typeof onChange === 'function') onChange(event.target.value)
        }

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
            if (typeof onBlur === 'function') onBlur(e)
            else setTouched && setTouched({ ...touched, [name]: true })
        }

        const handleKeyDown = (evt: any) => {
            if (evt.key === 'Enter' && typeof onEnter === 'function') {
                onEnter(evt)
            }
        }

        useEffect(() => {
            if (state?.[name] === null) {
                setState({ ...state, [name]: '' })
            }
        }, [state?.[name]]) // eslint-disable-line

        const isInvalid = touched && touched[name] && errors && errors[name]

        return (
            <Form.Group
                style={{ maxWidth: '600px' }}
                className={`${className} ${
                    isInvalid && !isFeedbackFixed && 'mb-4'
                } d-flex flex-1 mt-1 mb-1 justify-content-between align-items-baseline`}
            >
                {label && !isLabelHidden ? (
                    <Form.Label
                        style={{
                            maxWidth: isColumn ? '30%' : 'auto',
                            opacity: isRiskyParam ? '50%' : '100%',
                        }}
                        className="label-main mt-1 text-capital pe-2"
                    >
                        <span className="label-main-wrapper p-relative">
                            {label}: {isRequired && <RequiredMark />}
                        </span>
                    </Form.Label>
                ) : null}

                <div
                    style={{ width: '70%' }}
                    className={`d-flex ${!isColumn ? 'flex-1' : ''}`}
                >
                    <span className="d-flex flex-column w-100 p-relative">
                        {isRiskyParam && <div className="input-overlay" />}
                        <Form.Control
                            ref={ref}
                            className={`input-main ${
                                tooltipText && 'input-padding'
                            }`}
                            type={!isEmail ? 'text' : 'email'}
                            placeholder={placeholder}
                            value={state?.[name] ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            disabled={!!isDisabled}
                            isInvalid={isInvalid}
                            tabIndex={isRiskyParam ? -1 : 0}
                        />
                        <Form.Control.Feedback
                            className="input-feedback"
                            type="invalid"
                        >
                            <FormattedMessage
                                id={errMsg?.message || 'field-validation-error'}
                                values={errMsg?.values}
                                tagName="span"
                            />
                        </Form.Control.Feedback>
                        {tooltipText && (
                            <DescriptionBtnWithPopup
                                type={'default'}
                                header={label ?? ''}
                                description={tooltipText ?? ''}
                                className="mt-1 input-tooltip"
                            />
                        )}
                    </span>

                    {isRiskyParam && (
                        <RiskyParamUnlockBtn
                            onUnlockConfirm={handleUnlockRiskyParam}
                            changeWarning={changeWarning}
                        />
                    )}
                </div>
            </Form.Group>
        )
    })
)
